<template>
  <b-modal
    :visible="show"
    centered
    hide-header
    hide-footer
    no-fade
    size="lg"
    @hide="onHide"
  >
    <div class="bcm-root">
      <button class="bcm-close" @click="onHide" aria-label="Close">
        <b-icon icon="x" />
      </button>

      <div class="bcm-header">
        <div class="bcm-header__icon-wrap">
          <b-icon icon="envelope-fill" class="bcm-header__icon" />
        </div>
        <div>
          <p class="bcm-header__title">Billing Contacts</p>
          <!-- A LIMOSS invoice has no TPA: it is billed per underwriter and
               always goes to LimeIQ support, so its popup is read-only. -->
          <p class="bcm-header__sub" v-if="context && isLimoss">
            Underwriter - {{ context.underwriter_name || context.underwriter_rls_name || 'Unknown' }}
          </p>
          <p class="bcm-header__sub" v-else-if="context">
            {{ context.tpa_name || 'TPA' }}
            <span v-if="context.underwriter_name"> · {{ context.underwriter_name }}</span>
          </p>
          <p class="bcm-header__note" v-if="isLimoss">
            LIMOSS invoices are always sent to {{ LIMOSS_CONTACT }}.
          </p>
          <p class="bcm-header__note" v-else-if="companyLabel">
            Changes apply to all invoices billed to {{ companyLabel }}
          </p>
        </div>
      </div>

      <b-form-input
        v-if="!loading && !isLimoss && contacts.length > 0"
        v-model="search"
        size="sm"
        class="bcm-search"
        placeholder="Search by name or email"
        autocomplete="off"
      />

      <div v-if="toggleError" class="bcm-toggle-error" role="alert">{{ toggleError }}</div>

      <div v-if="canManage && !loading && !error" class="bcm-ext">
        <button
          v-if="!form"
          type="button"
          class="bcm-ext__add"
          title="Add external contact"
          :disabled="anySaving"
          @click="startAdd"
        >
          <b-icon icon="plus" /> Add external contact
        </button>
        <form v-else class="bcm-ext-form" novalidate @submit.prevent="onSubmitForm">
          <p class="bcm-ext-form__title">{{ form.id ? 'Edit external contact' : 'New external contact' }}</p>
          <b-form-input
            v-model="form.email"
            size="sm"
            placeholder="Email (required)"
            autocomplete="off"
            :state="formError ? false : null"
            :disabled="formSaving"
          />
          <b-form-input
            v-model="form.name"
            size="sm"
            placeholder="Name (optional)"
            autocomplete="off"
            :disabled="formSaving"
          />
          <div v-if="formError" class="bcm-toggle-error" role="alert">{{ formError }}</div>
          <div class="bcm-ext-form__actions">
            <b-button size="sm" variant="link" :disabled="formSaving" @click="cancelForm">Cancel</b-button>
            <b-button size="sm" variant="primary" type="submit" :disabled="formSaving">
              {{ formSaving ? 'Saving…' : (form.id ? 'Save' : 'Add') }}
            </b-button>
          </div>
        </form>
      </div>

      <div v-if="loading" class="bcm-state">Loading…</div>
      <div v-else-if="error" class="bcm-state bcm-state--error">{{ error }}</div>
      <div v-else-if="!contacts.length" class="bcm-state">No billing contacts on file.</div>
      <div v-else-if="!filteredContacts.length" class="bcm-state">No contacts match "{{ search }}".</div>

      <ul v-else class="bcm-list">
        <li v-for="contact in filteredContacts" :key="contactKey(contact)" class="bcm-list__item">
          <b-icon :icon="contact.kind === 'external' ? 'envelope' : 'person'" class="bcm-list__icon" />
          <div class="bcm-list__text">
            <span class="bcm-list__name" v-if="contact.name" :title="contact.name">{{ contact.name }}</span>
            <span class="bcm-list__email" :title="contact.email">{{ contact.email }}</span>
          </div>
          <span v-if="contact.kind === 'external'" class="bcm-tag bcm-tag--external">External</span>
          <template v-if="canManage && contact.kind === 'external' && contact.id">
            <span v-if="confirmDeleteId === contact.id" class="bcm-confirm">
              Delete?
              <button type="button" class="bcm-link bcm-link--danger" :disabled="formSaving" @click="onDelete(contact)">Yes</button>
              <button type="button" class="bcm-link" :disabled="formSaving" @click="confirmDeleteId = null">No</button>
            </span>
            <template v-else>
              <button
                type="button"
                class="bcm-icon-btn"
                title="Edit external contact"
                :disabled="anySaving"
                @click="startEdit(contact)"
              ><b-icon icon="pencil" /></button>
              <button
                type="button"
                class="bcm-icon-btn bcm-icon-btn--danger"
                title="Delete external contact"
                :disabled="anySaving"
                @click="askDelete(contact)"
              ><b-icon icon="trash" /></button>
            </template>
          </template>
          <base-switch
            v-if="canManage && contact.kind === 'user' && contact.member_id"
            :id="`bcm-billing-contact-${contact.member_id}`"
            :value="contact.is_billing_contact"
            onText=""
            offText=""
            :class="{ 'bcm-switch--saving': isSaving(contact), 'bcm-switch--busy': anySaving && !isSaving(contact) }"
            :title="contact.is_billing_contact ? 'Remove as billing contact' : 'Make billing contact'"
            @click="onToggle(contact, $event)"
          ></base-switch>
          <span
            v-else-if="contact.is_billing_contact"
            class="bcm-tag bcm-tag--on"
            title="Receives invoices for this company"
          >Billing Contact</span>
        </li>
      </ul>
    </div>
  </b-modal>
</template>

<script>
import { defineComponent, ref, computed, watch } from '@vue/composition-api'

import { BillingManagementService } from '@services/BillingManagementService'
import Members from '@services/Members'

// Every LIMOSS invoice goes here, whatever the underwriter (see Lincoln's
// _enrich_with_billing_contacts).
const LIMOSS_CONTACT = 'support@limeiq.com'

/**
 * Billing Contacts popup (LIQ-2710), opened from the ⋯ icon / "+N" link in
 * the Billing Contact cell of the Draft and Approved invoice tables.
 *
 * The list comes from GET /company/<id>/billing-contacts/ (LIQ-2709), which
 * already enforces who may see what: Superusers get every active user with
 * their billing flag plus external contacts; anyone else gets the current
 * billing contacts only (read-only, AC12). The order is the endpoint's
 * (billing contacts first) and is fixed when the popup opens -- the search
 * box only filters, it never re-sorts.
 *
 * `context` is the table row: tpa_company_id, tpa_name, underwriter_name and
 * billing_contact (the row's own emails, used as a fallback when the
 * endpoint cannot be reached).
 *
 * Superusers (`can_manage`) get a toggle per registered user (LIQ-2711). It
 * saves immediately through the same endpoint as the Companies > Users
 * toggle, so the Members flag stays the one source of truth; on failure the
 * toggle reverts and the error is shown. After each successful save the
 * popup emits `changed` with { companyId, emails } -- the company's billing
 * contact emails as now listed -- so the tables can update their rows.
 *
 * Superusers can also add, edit and delete external contacts (LIQ-2713)
 * through the LIQ-2712 endpoints. The inline form runs the server's checks
 * first; saves and deletes emit the same `changed` event.
 */
export default defineComponent({
  name: 'BillingContactsModal',

  props: {
    show: { type: Boolean, default: false },
    context: { type: Object, default: null },
  },

  setup(props, { emit }) {
    const loading = ref(false)
    const error = ref(null)
    const contacts = ref([])
    const canManage = ref(false)
    const companyName = ref('')
    const search = ref('')
    const toggleError = ref(null)
    // { member_id: true } while that user's toggle is saving
    const saving = ref({})
    // External contact add/edit form (LIQ-2713): null, or { id, email, name }
    // with id null when adding.
    const form = ref(null)
    const formError = ref(null)
    const formSaving = ref(false)
    const confirmDeleteId = ref(null)

    const isLimoss = computed(() => !!(props.context && props.context.billing_type === 'LIMOSS'))

    const companyLabel = computed(() =>
      companyName.value || (props.context && props.context.tpa_name) || '')

    const normalise = (s) => (s || '').toString().trim().toLowerCase()

    const filteredContacts = computed(() => {
      const term = normalise(search.value)
      if (!term) return contacts.value
      return contacts.value.filter(c =>
        normalise(c.name).includes(term) || normalise(c.email).includes(term))
    })

    const contactKey = (c) => `${c.kind}-${c.id || c.email}`

    // The row's own billing_contact emails, shown when the endpoint fails
    // so the popup still tells the user who the invoice goes to.
    const fallbackContacts = () => {
      const val = props.context && props.context.billing_contact
      const emails = Array.isArray(val) ? val : (typeof val === 'string' && val ? [val] : [])
      return emails.map(email => ({
        kind: 'user', id: null, name: null, email, is_billing_contact: true,
      }))
    }

    let requestSeq = 0
    const load = async () => {
      const seq = ++requestSeq
      // Reset every piece of state up front: a previous request that was
      // invalidated by onHide never reaches its finally block.
      loading.value = false
      search.value = ''
      error.value = null
      toggleError.value = null
      saving.value = {}
      form.value = null
      formError.value = null
      formSaving.value = false
      confirmDeleteId.value = null
      contacts.value = []
      canManage.value = false
      companyName.value = ''

      // LIMOSS: nothing to look up or manage - the recipient is fixed.
      if (isLimoss.value) {
        contacts.value = [{ kind: 'user', id: null, name: 'LimeIQ Support', email: LIMOSS_CONTACT, is_billing_contact: true }]
        return
      }

      const companyId = props.context && props.context.tpa_company_id
      if (!companyId) {
        contacts.value = fallbackContacts()
        return
      }

      loading.value = true
      try {
        const resp = await BillingManagementService.getBillingContacts(companyId)
        if (seq !== requestSeq) return
        const inner = resp.data && resp.data.data
        // The API renderer can wrap the payload in a second "data" envelope.
        const payload = inner && inner.contacts === undefined && inner.data ? inner.data : inner
        contacts.value = Array.isArray(payload && payload.contacts) ? payload.contacts : []
        canManage.value = !!(payload && payload.can_manage)
        companyName.value = (payload && payload.company_name) || ''
      } catch (e) {
        if (seq !== requestSeq) return
        const status = e && e.response && e.response.status
        if (status === 403) {
          error.value = 'You do not have permission to view billing contacts for this company.'
        } else {
          contacts.value = fallbackContacts()
          if (!contacts.value.length) error.value = 'Could not load billing contacts.'
        }
      } finally {
        if (seq === requestSeq) loading.value = false
      }
    }

    watch(() => props.show, (open) => {
      if (open) load()
    }, { immediate: true })

    // ── Billing contact toggle (LIQ-2711) ────────────────────────────────
    const isSaving = (contact) => !!saving.value[contact.member_id]
    // One save at a time across the popup, so the only unconfirmed change in
    // `contacts` is the one being saved and `changed` never carries another
    // contact's pending value.
    const anySaving = computed(() =>
      Object.keys(saving.value).length > 0 || formSaving.value)

    const setSaving = (memberId, on) => {
      const next = { ...saving.value }
      if (on) next[memberId] = true
      else delete next[memberId]
      saving.value = next
    }

    // The tables get the emails in the backend's recipient order (LIQ-2714,
    // billing_contacts._sort_key: name, else email, then email), not the
    // popup's on-screen order, so a row's first contact and the dispatch
    // email's Bill To line match what a refresh shows.
    const recipientKey = (c) => {
      const email = (c.email || '').trim().toLowerCase()
      return [(c.name || '').trim().toLowerCase() || email, email]
    }
    const compareRecipients = (a, b) => {
      const [ka, kb] = [recipientKey(a), recipientKey(b)]
      for (let i = 0; i < ka.length; i++) {
        if (ka[i] < kb[i]) return -1
        if (ka[i] > kb[i]) return 1
      }
      return 0
    }
    const billingEmailsOf = (list) =>
      list.filter(c => c.is_billing_contact).sort(compareRecipients).map(c => c.email)
    const billingEmails = () => billingEmailsOf(contacts.value)

    const onToggle = async (contact, event) => {
      // Let the click flip the checkbox: preventDefault would have the browser
      // put it back after Vue had drawn the new state, leaving the switch
      // showing the old value until the popup was reopened. Only a click
      // that is ignored keeps it where it was. A failed save reverts
      // is_billing_contact below and Vue moves the checkbox back.
      if (anySaving.value) {
        if (event) event.preventDefault()
        return
      }

      const seq = requestSeq
      const companyId = props.context && props.context.tpa_company_id
      const previous = contact.is_billing_contact
      const next = !previous

      toggleError.value = null
      contact.is_billing_contact = next
      setSaving(contact.member_id, true)
      // Snapshot now: if the popup is closed or reopened for another row
      // before the save returns, `contacts` no longer describes this company.
      const emailsIfSaved = billingEmails()

      let message = null
      try {
        const resp = await Members.toggleBillingContactStatus(contact.member_id, next)
        // The "last billing contact" guard answers HTTP 200 with a 400 in
        // the body (same handling as TableCompanyAdmins.vue).
        const body = resp && resp.data && resp.data.data
        if (body && body.status && body.status !== 200) {
          message = body.message || 'Failed to update billing contact.'
        }
      } catch (e) {
        const status = e && e.response && e.response.status
        message = status === 403
          ? 'Only Superusers can change billing contacts.'
          : 'Failed to update billing contact. Please try again.'
      }

      // The popup was closed or reopened for another row while saving: leave
      // its (reset) state alone, but a save the server accepted must still
      // reach the table.
      if (seq !== requestSeq) {
        if (!message) emit('changed', { companyId, emails: emailsIfSaved })
        return
      }
      setSaving(contact.member_id, false)

      if (message) {
        contact.is_billing_contact = previous
        toggleError.value = message
        return
      }
      emit('changed', { companyId, emails: emailsIfSaved })
    }

    // ── External contacts (LIQ-2713) ─────────────────────────────────────
    // The checks mirror validate_external_contact in company/billing_contacts.py
    // so most mistakes are caught before a request; the server stays the
    // authority and its message is shown when it rejects a save.
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const EMAIL_MAX = 254
    const NAME_MAX = 300

    const validateForm = ({ id, email, name }) => {
      const cleanEmail = normalise(email)
      const cleanName = (name || '').toString().trim() || null
      if (!cleanEmail) return { message: 'Email is required.' }
      if (!EMAIL_RE.test(cleanEmail)) return { message: `"${cleanEmail}" is not a valid email address.` }
      if (cleanEmail.length > EMAIL_MAX) return { message: 'Email is too long.' }
      if (cleanName && cleanName.length > NAME_MAX) {
        return { message: `Name must be at most ${NAME_MAX} characters.` }
      }
      if (contacts.value.some(c => c.kind === 'user' && normalise(c.email) === cleanEmail)) {
        return {
          message: `${cleanEmail} belongs to a registered user at this company. ` +
            "Switch on that user's billing contact toggle instead.",
        }
      }
      if (contacts.value.some(c =>
        c.kind === 'external' && c.id !== id && normalise(c.email) === cleanEmail)) {
        return { message: `${cleanEmail} is already an external billing contact for this company.` }
      }
      return { email: cleanEmail, name: cleanName }
    }

    // Error bodies arrive as { status, message } inside the API renderer's
    // "data" envelope.
    const serverMessage = (e, fallback) => {
      const body = e && e.response && e.response.data
      const inner = body && body.data
      return (inner && inner.message) || (body && body.message) || fallback
    }

    const startAdd = () => {
      confirmDeleteId.value = null
      formError.value = null
      form.value = { id: null, email: '', name: '' }
    }

    const startEdit = (contact) => {
      confirmDeleteId.value = null
      formError.value = null
      form.value = { id: contact.id, email: contact.email, name: contact.name || '' }
    }

    const cancelForm = () => {
      form.value = null
      formError.value = null
    }

    const askDelete = (contact) => {
      form.value = null
      formError.value = null
      confirmDeleteId.value = contact.id
    }

    const onSubmitForm = async () => {
      if (!form.value || anySaving.value) return
      const result = validateForm(form.value)
      if (result.message) {
        formError.value = result.message
        return
      }

      const seq = requestSeq
      const companyId = props.context && props.context.tpa_company_id
      // Snapshot: the popup may be closed or reopened for another company
      // before the save returns (see onToggle).
      const before = contacts.value
      const editingId = form.value.id
      const data = { email: result.email, name: result.name }
      formError.value = null
      formSaving.value = true

      let saved = null
      let message = null
      try {
        const resp = editingId
          ? await BillingManagementService.updateExternalBillingContact(companyId, editingId, data)
          : await BillingManagementService.addExternalBillingContact(companyId, data)
        const inner = resp && resp.data && resp.data.data
        saved = (inner && inner.data) || { kind: 'external', id: editingId, ...data, is_billing_contact: true }
      } catch (e) {
        const status = e && e.response && e.response.status
        message = status === 403
          ? 'Only Superusers can manage external billing contacts.'
          : serverMessage(e, 'Failed to save the contact. Please try again.')
      }

      let after = null
      if (!message) {
        after = before.filter(c => !(c.kind === 'external' && c.id === saved.id))
        if (editingId) {
          const idx = before.findIndex(c => c.kind === 'external' && c.id === saved.id)
          after.splice(idx < 0 ? 0 : idx, 0, saved)
        } else {
          // AC7: a new contact goes to the top of the list.
          after.unshift(saved)
        }
      }

      // Closed or reopened while saving: leave the (reset) popup alone, but a
      // save the server accepted must still reach the tables.
      if (seq !== requestSeq) {
        if (after) emit('changed', { companyId, emails: billingEmailsOf(after) })
        return
      }
      formSaving.value = false
      if (message) {
        formError.value = message
        return
      }
      contacts.value = after
      form.value = null
      emit('changed', { companyId, emails: billingEmailsOf(after) })
    }

    // Same wording as the server's guard (billing_contacts.LAST_CONTACT_MESSAGE).
    const LAST_CONTACT_MESSAGE =
      'At least one billing contact must remain: a registered user with billing contact switched on ' +
      'or an external contact. Add another billing contact before removing this one.'

    const onDelete = async (contact) => {
      if (anySaving.value) return
      // LIQ-2712: never leave the company with nobody to invoice. The server
      // enforces this too; checking here just answers without a round trip.
      const othersLeft = contacts.value.filter(c =>
        c.is_billing_contact && !(c.kind === 'external' && c.id === contact.id)).length
      if (!othersLeft) {
        confirmDeleteId.value = null
        toggleError.value = LAST_CONTACT_MESSAGE
        return
      }
      const seq = requestSeq
      const companyId = props.context && props.context.tpa_company_id
      const before = contacts.value
      toggleError.value = null
      formSaving.value = true

      let message = null
      try {
        await BillingManagementService.deleteExternalBillingContact(companyId, contact.id)
      } catch (e) {
        const status = e && e.response && e.response.status
        // Already gone (another Superuser deleted it): treat as deleted.
        if (status !== 404) {
          message = status === 403
            ? 'Only Superusers can manage external billing contacts.'
            : serverMessage(e, 'Failed to delete the contact. Please try again.')
        }
      }

      const after = message
        ? null
        : before.filter(c => !(c.kind === 'external' && c.id === contact.id))

      // Same as a save: a delete the server accepted still reaches the tables.
      if (seq !== requestSeq) {
        if (after) emit('changed', { companyId, emails: billingEmailsOf(after) })
        return
      }
      formSaving.value = false
      confirmDeleteId.value = null
      if (message) {
        toggleError.value = message
        return
      }
      contacts.value = after
      emit('changed', { companyId, emails: billingEmailsOf(after) })
    }

    const onHide = () => {
      requestSeq++
      emit('hide')
    }

    return {
      loading, error, contacts, canManage, companyLabel, search, isLimoss, LIMOSS_CONTACT,
      filteredContacts, contactKey, onHide,
      toggleError, isSaving, anySaving, onToggle,
      form, formError, formSaving, confirmDeleteId,
      startAdd, startEdit, cancelForm, askDelete, onSubmitForm, onDelete,
    }
  },
})
</script>

<style scoped>
.bcm-root { position: relative; padding: 4px 0 0; }
.bcm-close {
  position: absolute; top: -8px; right: -8px;
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 50%; border: none;
  background: transparent; color: #8898aa; font-size: 1.4rem;
  cursor: pointer; padding: 0; line-height: 1;
  transition: background 0.15s, color 0.15s;
}
.bcm-close:hover { background: #f0f0f0; color: #32325d; }

.bcm-header { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 12px; }
.bcm-header__icon-wrap {
  display: flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: 50%;
  background: #e8f4f0; flex-shrink: 0;
}
.bcm-header__icon { font-size: 1.2rem; color: #1c4a50; }
.bcm-header__title { font-size: 0.95rem; font-weight: 700; color: #32325d; margin: 0 0 2px; }
.bcm-header__sub { font-size: 0.8rem; color: #525f7f; margin: 0; }
.bcm-header__note { font-size: 0.72rem; color: #8898aa; margin: 4px 0 0; font-style: italic; }

.bcm-search { margin-bottom: 8px; font-size: 12px; }

.bcm-state { padding: 16px 10px; text-align: center; color: #8898aa; font-size: 12px; }
.bcm-state--error { color: #dc3545; }

.bcm-toggle-error {
  margin-bottom: 8px; padding: 6px 10px; border-radius: 6px;
  background: #fdecee; color: #dc3545; font-size: 12px;
}
.bcm-list__item .custom-toggle { flex-shrink: 0; margin: 0; }
.bcm-switch--saving { opacity: 0.5; pointer-events: none; }
.bcm-switch--busy { pointer-events: none; }

.bcm-list { list-style: none; margin: 0; padding: 0; max-height: 50vh; overflow-y: auto; }
.bcm-list__item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border-radius: 6px; font-size: 13px; color: #32325d;
  border-bottom: 1px solid #f1f3f5;
}
.bcm-list__item:last-child { border-bottom: none; }
.bcm-list__icon { color: #1c4a50; font-size: 12px; flex-shrink: 0; }
/* The text column keeps a real width so the tags and buttons beside it can't
   squeeze a name or email down to a few letters; long values wrap. */
.bcm-list__text { display: flex; flex-direction: column; min-width: 220px; flex: 1 1 auto; }
.bcm-list__name { font-weight: 600; font-size: 13px; line-height: 1.3; overflow-wrap: anywhere; }
.bcm-list__email { font-size: 12px; color: #525f7f; overflow-wrap: anywhere; }

.bcm-tag {
  flex-shrink: 0; display: inline-block; padding: 1px 6px; border-radius: 10px;
  font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em;
}
.bcm-tag--external { background: #f1f3f5; color: #525f7f; }
.bcm-tag--on { background: #e8f4f0; color: #1c4a50; }

.bcm-ext { margin-bottom: 8px; }
.bcm-ext__add {
  display: inline-flex; align-items: center; gap: 4px;
  border: none; background: transparent; padding: 2px 0;
  color: #1c4a50; font-size: 12px; font-weight: 600; cursor: pointer;
}
.bcm-ext__add:disabled { opacity: 0.5; cursor: default; }
.bcm-ext-form {
  display: flex; flex-direction: column; gap: 6px;
  padding: 8px 10px; border: 1px solid #e9ecef; border-radius: 6px; background: #f8f9fe;
}
.bcm-ext-form .form-control { font-size: 12px; }
.bcm-ext-form .bcm-toggle-error { margin-bottom: 0; }
.bcm-ext-form__title { margin: 0; font-size: 12px; font-weight: 700; color: #32325d; }
.bcm-ext-form__actions { display: flex; justify-content: flex-end; gap: 4px; }

.bcm-icon-btn {
  flex-shrink: 0; border: none; background: transparent; padding: 2px 4px;
  color: #8898aa; font-size: 12px; cursor: pointer; line-height: 1;
}
.bcm-icon-btn:hover { color: #1c4a50; }
.bcm-icon-btn--danger:hover { color: #dc3545; }
.bcm-icon-btn:disabled { opacity: 0.4; cursor: default; }

.bcm-confirm { flex-shrink: 0; font-size: 11px; color: #dc3545; white-space: nowrap; }
.bcm-link {
  border: none; background: transparent; padding: 0 2px;
  font-size: 11px; font-weight: 600; color: #525f7f; cursor: pointer;
}
.bcm-link--danger { color: #dc3545; }
</style>
